/**
 * Trust Engine Core Library
 * Implements: Trust Graph, Authority States, Signal Authority, Absence Intelligence
 */

import { getSupabaseClient } from "@/lib/supabase/loader";

// =============================================
// TYPES
// =============================================

export type TrustEdgeType = 
  | 'guardian' | 'delegate' | 'executor' | 'medical_proxy' 
  | 'financial_proxy' | 'emergency_contact' | 'witness' 
  | 'institution' | 'fallback';

export type TrustScope = 
  | 'full' | 'financial' | 'medical' | 'legal' 
  | 'childcare' | 'business' | 'digital' | 'physical' | 'notification';

export type AuthorityStateType =
  | 'fully_active' | 'soft_delegation' | 'temporary_absence'
  | 'extended_absence' | 'restricted_continuity' | 'emergency_mode'
  | 'medical_incapacity' | 'legal_restriction' | 'institution_controlled'
  | 'court_supervised' | 'disputed' | 'frozen' | 'deceased';

export type SignalType =
  | 'authority_valid' | 'authority_expired' | 'authority_limited'
  | 'authority_pending' | 'authority_revoked' | 'action_permitted'
  | 'action_denied' | 'action_review_required' | 'state_changed'
  | 'trigger_fired' | 'confirmation_required' | 'court_override'
  | 'emergency_active' | 'identity_verified' | 'absence_detected' | 'fraud_warning';

export type AbsenceLevel =
  | 'present' | 'busy' | 'soft_absence' | 'silent_absence'
  | 'extended_silence' | 'partial_incapacity' | 'full_incapacity'
  | 'legal_unavailability' | 'missing' | 'presumed_deceased' | 'confirmed_deceased';

export interface TrustEdge {
  id: string;
  grantor_id: string;
  trustee_id: string;
  plan_id?: string;
  edge_type: TrustEdgeType;
  scopes: TrustScope[];
  weight: number;
  priority: number;
  max_amount_kes?: number;
  max_duration_days?: number;
  requires_co_trustee: boolean;
  co_trustee_id?: string;
  activation_trigger_id?: string;
  activation_delay_hours: number;
  requires_authority_state?: string[];
  cascade_order?: number;
  fallback_edge_id?: string;
  auto_cascade_after_hours?: number;
  revocable_by_grantor: boolean;
  revocable_by_trustee: boolean;
  revoked_at?: string;
  revoked_reason?: string;
  valid_from: string;
  valid_until?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuthorityState {
  id: string;
  user_id: string;
  plan_id?: string;
  current_state: AuthorityStateType;
  previous_state?: AuthorityStateType;
  state_reason?: string;
  state_evidence?: string;
  initiated_by?: string;
  initiated_by_type?: string;
  requires_confirmation: boolean;
  confirmation_group_id?: string;
  confirmations_received: number;
  confirmations_required: number;
  effective_from: string;
  effective_until?: string;
  auto_revert_to?: AuthorityStateType;
  auto_revert_at?: string;
  allowed_actions: string[];
  blocked_actions: string[];
  audit_level: 'minimal' | 'standard' | 'enhanced' | 'forensic';
  allow_institutional_access: boolean;
  require_institutional_approval: boolean;
  notify_institutions: boolean;
  court_order_reference?: string;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuthoritySignal {
  id: string;
  signal_type: SignalType;
  signal_code: string;
  subject_user_id: string;
  subject_plan_id?: string;
  subject_trustee_id?: string;
  authority_scope?: TrustScope[];
  authority_valid_until?: string;
  action_type?: string;
  action_scope?: string;
  action_limit_kes?: number;
  conditions: Record<string, unknown>;
  restrictions?: string[];
  is_verified: boolean;
  signal_hash: string;
  issued_at: string;
  valid_from: string;
  valid_until: string;
  status: 'active' | 'consumed' | 'expired' | 'revoked' | 'superseded';
  created_at: string;
}

export interface AbsenceState {
  id: string;
  user_id: string;
  current_level: AbsenceLevel;
  previous_level?: AbsenceLevel;
  detection_method?: string;
  evidence_type?: string;
  evidence_description?: string;
  last_activity_at: string;
  last_login_at?: string;
  last_check_in_at?: string;
  soft_absence_days: number;
  silent_absence_days: number;
  extended_silence_days: number;
  expected_return_at?: string;
  escalation_level: number;
  auto_delegate_to?: string;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

export interface TrustAuthorityCheck {
  authorized: boolean;
  reason?: string;
  edge_id?: string;
  edge_type?: TrustEdgeType;
  weight?: number;
  max_amount_kes?: number;
  valid_until?: string;
  co_trustee_id?: string;
  signal_code: string;
}

// =============================================
// Helper to bypass TypeScript for new tables
// =============================================
/* eslint-disable @typescript-eslint/no-explicit-any */
function fromTable(table: string) {
  return (supabase as any).from(table);
}

function rpcCall(fn: string, params: Record<string, any>) {
  return (supabase as any).rpc(fn, params);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// =============================================
// TRUST GRAPH FUNCTIONS
// =============================================

export async function getTrustEdges(grantorId?: string): Promise<{ data: TrustEdge[] | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  let query = fromTable('trust_edges')
    .select('*')
    .eq('is_active', true)
    .order('priority', { ascending: true });
  
  if (grantorId) {
    query = query.eq('grantor_id', grantorId);
  } else {
    query = query.eq('grantor_id', user.id);
  }

  const { data, error } = await query;
  return { data, error };
}

export async function createTrustEdge(edge: {
  trustee_id: string;
  plan_id?: string;
  edge_type: TrustEdgeType;
  scopes: TrustScope[];
  weight?: number;
  priority?: number;
  max_amount_kes?: number;
  max_duration_days?: number;
  requires_co_trustee?: boolean;
  co_trustee_id?: string;
  valid_until?: string;
}): Promise<{ data: TrustEdge | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await fromTable('trust_edges')
    .insert({
      grantor_id: user.id,
      ...edge,
    })
    .select()
    .single();

  return { data, error };
}

export async function updateTrustEdge(
  id: string,
  updates: Partial<TrustEdge>
): Promise<{ error: Error | null }> {
  const { error } = await fromTable('trust_edges')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  
  return { error };
}

export async function revokeTrustEdge(
  id: string,
  reason: string
): Promise<{ error: Error | null }> {
  const { error } = await fromTable('trust_edges')
    .update({ 
      is_active: false,
      revoked_at: new Date().toISOString(),
      revoked_reason: reason,
      updated_at: new Date().toISOString()
    })
    .eq('id', id);
  
  return { error };
}

export async function checkTrusteeAuthority(
  grantorId: string,
  trusteeId: string,
  scope: TrustScope,
  action?: string
): Promise<{ data: TrustAuthorityCheck | null; error: Error | null }> {
  const { data, error } = await rpcCall('check_trustee_authority', {
    p_grantor_id: grantorId,
    p_trustee_id: trusteeId,
    p_scope: scope,
    p_action: action || null,
  });

  return { data, error };
}

// =============================================
// AUTHORITY STATE FUNCTIONS
// =============================================

export async function getAuthorityState(): Promise<{ data: AuthorityState | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await fromTable('authority_states')
    .select('*')
    .eq('user_id', user.id)
    .eq('is_current', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  return { data, error };
}

export async function getCurrentAuthorityStateType(): Promise<AuthorityStateType> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 'fully_active';

  const { data, error } = await rpcCall('get_authority_state', {
    p_user_id: user.id,
  });

  if (error || !data) return 'fully_active';
  return data as AuthorityStateType;
}

export async function canTransitionState(
  toState: AuthorityStateType,
  initiatorType: string
): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { data } = await rpcCall('can_transition_state', {
    p_user_id: user.id,
    p_to_state: toState,
    p_initiator_type: initiatorType,
  });

  return data ?? false;
}

export async function transitionAuthorityState(params: {
  to_state: AuthorityStateType;
  reason?: string;
  evidence?: string;
  effective_until?: string;
  auto_revert_to?: AuthorityStateType;
  auto_revert_at?: string;
}): Promise<{ data: AuthorityState | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  // Get current state
  const currentState = await getCurrentAuthorityStateType();
  
  // Check if transition is valid
  const canTransition = await canTransitionState(params.to_state, 'self');
  if (!canTransition) {
    return { data: null, error: new Error('Invalid state transition') };
  }

  // Mark previous state as not current
  await fromTable('authority_states')
    .update({ is_current: false })
    .eq('user_id', user.id)
    .eq('is_current', true);

  // Create new state
  const { data, error } = await fromTable('authority_states')
    .insert({
      user_id: user.id,
      current_state: params.to_state,
      previous_state: currentState,
      state_reason: params.reason,
      state_evidence: params.evidence,
      initiated_by_type: 'self',
      effective_until: params.effective_until,
      auto_revert_to: params.auto_revert_to,
      auto_revert_at: params.auto_revert_at,
      is_current: true,
    })
    .select()
    .single();

  return { data, error };
}

export async function getValidStateTransitions(): Promise<{ data: Array<{ from_state: AuthorityStateType; to_state: AuthorityStateType; allowed_initiators: string[] }> | null; error: Error | null }> {
  const currentState = await getCurrentAuthorityStateType();

  const { data, error } = await fromTable('authority_state_transitions')
    .select('from_state, to_state, allowed_initiators')
    .eq('from_state', currentState)
    .eq('is_active', true);

  return { data, error };
}

// =============================================
// SIGNAL AUTHORITY FUNCTIONS
// =============================================

export async function getActiveSignals(): Promise<{ data: AuthoritySignal[] | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await fromTable('authority_signals')
    .select('*')
    .eq('subject_user_id', user.id)
    .eq('status', 'active')
    .gt('valid_until', new Date().toISOString())
    .order('created_at', { ascending: false });

  return { data, error };
}

export async function generateSignal(params: {
  signal_type: SignalType;
  signal_code: string;
  scope?: TrustScope[];
  valid_hours?: number;
  conditions?: Record<string, unknown>;
}): Promise<{ data: AuthoritySignal | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await rpcCall('generate_signal', {
    p_subject_user_id: user.id,
    p_signal_type: params.signal_type,
    p_signal_code: params.signal_code,
    p_scope: params.scope || ['notification'],
    p_valid_hours: params.valid_hours || 24,
    p_conditions: params.conditions || {},
  });

  return { data, error };
}

export async function revokeSignal(
  signalId: string,
  reason: string
): Promise<{ error: Error | null }> {
  const { error } = await fromTable('authority_signals')
    .update({
      status: 'revoked',
      revoked_at: new Date().toISOString(),
      revocation_reason: reason,
    })
    .eq('id', signalId);

  return { error };
}

// =============================================
// ABSENCE INTELLIGENCE FUNCTIONS
// =============================================

export async function getAbsenceState(): Promise<{ data: AbsenceState | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await fromTable('absence_states')
    .select('*')
    .eq('user_id', user.id)
    .eq('is_current', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  return { data, error };
}

export async function getCurrentAbsenceLevel(): Promise<AbsenceLevel> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 'present';

  const { data, error } = await rpcCall('get_absence_level', {
    p_user_id: user.id,
  });

  if (error || !data) return 'present';
  return data as AbsenceLevel;
}

export async function checkIn(params?: {
  method?: string;
  message?: string;
}): Promise<{ data: { id: string; created_at: string } | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await rpcCall('record_check_in', {
    p_user_id: user.id,
    p_method: params?.method || 'app_login',
    p_ip: null,
    p_user_agent: navigator.userAgent,
    p_message: params?.message || null,
  });

  return { data, error };
}

export async function updateAbsenceSettings(settings: {
  soft_absence_days?: number;
  silent_absence_days?: number;
  extended_silence_days?: number;
  auto_delegate_to?: string;
  auto_response_message?: string;
}): Promise<{ error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: new Error('Not authenticated') };

  const { error } = await fromTable('absence_states')
    .update({
      ...settings,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', user.id)
    .eq('is_current', true);

  return { error };
}

export async function setExpectedReturn(
  returnDate: string,
  message?: string
): Promise<{ error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: new Error('Not authenticated') };

  // Update absence state
  const { error } = await fromTable('absence_states')
    .update({
      expected_return_at: returnDate,
      auto_response_message: message,
      current_level: 'soft_absence',
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', user.id)
    .eq('is_current', true);

  return { error };
}

export async function getAbsenceRules(): Promise<{ data: Array<{
  id: string;
  rule_name: string;
  from_level: AbsenceLevel;
  to_level: AbsenceLevel;
  inactivity_days?: number;
  notify_trustees: boolean;
  is_active: boolean;
}> | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await fromTable('absence_rules')
    .select('*')
    .eq('user_id', user.id)
    .order('from_level', { ascending: true });

  return { data, error };
}

export async function createAbsenceRule(rule: {
  rule_name: string;
  from_level: AbsenceLevel;
  to_level: AbsenceLevel;
  inactivity_days?: number;
  missed_check_ins?: number;
  notify_trustees?: boolean;
  notify_institutions?: boolean;
  change_authority_state?: boolean;
  target_authority_state?: AuthorityStateType;
}): Promise<{ data: { id: string } | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await fromTable('absence_rules')
    .insert({
      user_id: user.id,
      ...rule,
    })
    .select('id')
    .single();

  return { data, error };
}

// =============================================
// COMBINED STATUS FUNCTIONS
// =============================================

export interface UserTrustStatus {
  authorityState: AuthorityStateType;
  absenceLevel: AbsenceLevel;
  activeTrustEdges: number;
  activeSignals: number;
  lastCheckIn?: string;
  expectedReturn?: string;
}

export async function getUserTrustStatus(): Promise<{ data: UserTrustStatus | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  try {
    const [authorityState, absenceLevel, trustEdges, signals, absenceState] = await Promise.all([
      getCurrentAuthorityStateType(),
      getCurrentAbsenceLevel(),
      getTrustEdges(),
      getActiveSignals(),
      getAbsenceState(),
    ]);

    return {
      data: {
        authorityState,
        absenceLevel,
        activeTrustEdges: trustEdges.data?.length ?? 0,
        activeSignals: signals.data?.length ?? 0,
        lastCheckIn: absenceState.data?.last_check_in_at,
        expectedReturn: absenceState.data?.expected_return_at,
      },
      error: null,
    };
  } catch (err) {
    return { data: null, error: err as Error };
  }
}
